/* eslint-disable react/no-array-index-key */
import * as React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { Input } from '@lightspeed/flame/Input';
import { Select } from '@lightspeed/flame/Select';
import { Text } from '@lightspeed/flame/Text';

import SectionTitle from 'root/apps/PurchaseOrder/ShipmentHistory/components/SectionTitle';
import { OverviewDataContext } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';
import { DatePickerFormat } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';
import TooltipHelp from 'root/apps/Tooltip/components/TooltipHelp';

import { ShippingDateReceivedDatePicker, ShippingInvoiceDueDatePicker } from './ShippingDatePicker';

import type { ShippingStatus } from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  isMultiCurrency: boolean;
}

const ShipmentInformation: React.FC<Props> = ({ isMultiCurrency }) => {
  const {
    data,
    isLoading,
    isInitialLoading,
    isDisabled,
    savingKeys,
    hasPurchaseOrderLandedCostsManualDiscountAdjustment,
    saveOverviewData,
  } = React.useContext(OverviewDataContext);

  const inputRef = React.useRef<HTMLInputElement>(null);

  const isPanDetectionActive = window.lsCloud.Modules.hasModule('data.pan_detection.warn');

  const { date_format: dateFormat, lang } = window.merchantos.locale;
  const datePickerFormat = DatePickerFormat(dateFormat);

  const applyPanErrorStyleIfVisible = () => {
    const element = document.querySelector('.sensitive-info-error') as HTMLElement;
    if (element) {
      element.style.paddingLeft = '15px';
    }
  };

  React.useEffect(() => {
    if (!isInitialLoading) {
      const inputField = inputRef.current as HTMLElement;
      window.validateField(inputField, {
        containerSelector: document.getElementById('shipment-error-container'),
      });
      applyPanErrorStyleIfVisible();
    }
  }, [isInitialLoading]);

  return (
    <Box style={{ margin: '36px 0', width: '100%' }}>
      <Flex justifyContent="space-between">
        {!isInitialLoading ? (
          <SectionTitle>Shipment information</SectionTitle>
        ) : (
          <Box>
            <Bone height={4} width="150px" data-test="shipment-information-loading" />
          </Box>
        )}
        <Card style={{ flex: 2, maxWidth: '60%' }} id="shipment-error-container">
          {isInitialLoading ? (
            new Array(6).fill(0).map((_v, index, row) => (
              <React.Fragment key={index}>
                <CardSection style={{ padding: '1rem' }}>
                  <Flex>
                    <Bone height={3} width="30%" style={{ marginRight: '15%' }} />
                    <Bone height={3} width="30%" />
                  </Flex>
                </CardSection>
                {index + 1 !== row.length ? <Divider /> : null}
              </React.Fragment>
            ))
          ) : (
            <>
              <CardSection style={{ padding: '1rem' }}>
                <Flex alignItems="center">
                  <Text style={{ width: '35%' }} fontWeight="bold">
                    Status{' '}
                    <TooltipHelp
                      title="Enable Lightspeed accounting"
                      content="Enable the Lightspeed accounting integration in Lightspeed accounting settings to sync shipments with your accounting software. This feature is exclusive to subscribed merchants."
                      button={
                        <Button
                          href="https://retail-support.lightspeedhq.com/hc/en-us/articles/360007754194-General-Settings"
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                        >
                          <IconExternalLink /> Go to Help Center
                        </Button>
                      }
                    />
                  </Text>
                  <Select
                    data-test="shipment-information-select"
                    style={{ cursor: 'pointer' }}
                    width="fit-content"
                    value={data?.status}
                    disabled={savingKeys?.includes('status')}
                    onChange={(event) => {
                      const status = event.target.value as ShippingStatus;

                      saveOverviewData({
                        orderId: data!.orderID,
                        shipmentId: data!.shipmentID,
                        payload: { status },
                      });
                    }}
                  >
                    <option value="received">Received</option>
                    <option value="finished">Finished</option>
                  </Select>
                </Flex>
              </CardSection>
              <Divider />
              <CardSection>
                <Flex alignItems="center">
                  <Text style={{ width: '35%', padding: '0.25rem' }} fontWeight="bold">
                    Date received
                  </Text>
                  <ShippingDateReceivedDatePicker
                    datePickerFormat={datePickerFormat}
                    lang={lang}
                    isDisabled={isDisabled! || savingKeys?.includes('receptionDate')!}
                  />
                </Flex>
              </CardSection>
              <Divider />
              <CardSection>
                <Flex alignItems="center">
                  <Text style={{ width: '35%', padding: '0.25rem' }} fontWeight="bold">
                    Invoice due date
                  </Text>
                  <ShippingInvoiceDueDatePicker
                    datePickerFormat={datePickerFormat}
                    lang={lang}
                    isDisabled={isDisabled! || savingKeys?.includes('paymentDueDate')!}
                  />
                </Flex>
              </CardSection>
              <Divider />
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text style={{ width: '35%' }} fontWeight="bold">
                    Vendor
                  </Text>
                  <Text className="notranslate" data-test="shipment-information-vendor">
                    {data?.vendorName || '-'}
                  </Text>
                </Flex>
              </CardSection>
              <Divider />
              {hasPurchaseOrderLandedCostsManualDiscountAdjustment && isMultiCurrency ? (
                <>
                  <CardSection style={{ padding: '1rem' }}>
                    <Flex>
                      <Text
                        style={{ width: '35%' }}
                        fontWeight="bold"
                        className="notranslate"
                        data-test="shipment-information-vendor-currency"
                      >
                        Vendor currency
                      </Text>
                      <Text className="notranslate">
                        <span
                          style={{ fontWeight: 'bold' }}
                          data-test="shipment-information-vendor-currency-code"
                        >
                          {data?.vendorCurrencyCode}
                        </span>{' '}
                        <span data-test="shipment-information-vendor-currency-rate">
                          {data?.vendorCurrencyRate}
                        </span>
                      </Text>
                    </Flex>
                  </CardSection>
                  <Divider />
                </>
              ) : null}
              <CardSection style={{ padding: '1rem' }} id="shipment-reference-number-container">
                <Flex alignItems="center" justifyContent="space-between">
                  <Text style={{ width: '25%' }} fontWeight="bold">
                    Shipment reference number
                  </Text>
                  <Input
                    ref={inputRef}
                    id="shipment-reference-number"
                    {...(isPanDetectionActive && { 'data-validation': 'checkForCreditCardInfo' })}
                    data-test="shipment-refnum-input"
                    disabled={isDisabled || savingKeys?.includes('shipmentPackingRefNum')}
                    defaultValue={data?.shipmentPackingRefNum}
                    width="65%"
                    onChange={(event) => {
                      if (isPanDetectionActive) {
                        window.validateField(event.target, {
                          containerSelector: document.getElementById('shipment-error-container'),
                        });
                        applyPanErrorStyleIfVisible();
                      }
                    }}
                    onBlur={(event) => {
                      if (isPanDetectionActive) {
                        window.validateField(event.target, {
                          containerSelector: document.getElementById('shipment-error-container'),
                        });
                        applyPanErrorStyleIfVisible();
                      }
                      const shipmentPackingRefNum = event.target.value;
                      if (shipmentPackingRefNum !== data?.shipmentPackingRefNum) {
                        saveOverviewData({
                          orderId: data!.orderID,
                          shipmentId: data!.shipmentID,
                          payload: { shipmentPackingRefNum },
                        });
                      }
                    }}
                  />
                </Flex>
              </CardSection>
              <Divider />
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text style={{ width: '35%' }} fontWeight="bold">
                    Item quantity
                  </Text>
                  <Text
                    className="notranslate"
                    data-test="shipment-information-item-quantity"
                    style={{ opacity: isLoading ? 0.5 : 1 }}
                  >
                    {data?.sumQtyReceived || '-'}
                  </Text>
                </Flex>
              </CardSection>
            </>
          )}
        </Card>
      </Flex>
    </Box>
  );
};

export default ShipmentInformation;
